import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Send } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import SEO from '@/components/SEO';
import { Link } from 'react-router-dom';
import { ModernLoader } from '@/components/ui/modern-loader';

interface Message {
  id: string;
  content: string;
  created_at: string;
  sender_id: string;
  recipient_id: string;
  read: boolean;
}

export default function MessageThread() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [recipientProfile, setRecipientProfile] = useState<{
    username: string;
    avatar_url?: string;
  } | null>(null);
  const firstUnreadRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!user || !userId) {
      navigate('/inbox');
      return;
    }

    fetchRecipientProfile();
    fetchMessages();
    markMessagesAsRead();
    setupRealtimeSubscription();
  }, [user, userId]);

  const fetchRecipientProfile = async () => {
    if (!userId) return;

    const { data, error } = await supabase
      .from('public_profile_data')
      .select('username, avatar_url')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Error fetching recipient profile:', error);
      toast.error('Failed to load user profile');
      navigate('/inbox');
      return;
    }

    setRecipientProfile(data);
  };

  const fetchMessages = async () => {
    if (!user || !userId) return;

    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .or(`and(sender_id.eq.${user.id},recipient_id.eq.${userId}),and(sender_id.eq.${userId},recipient_id.eq.${user.id})`)
        .order('created_at', { ascending: true });

      if (error) throw error;

      setMessages(data || []);
      
      // Scroll to first unread message or bottom after messages load
      setTimeout(() => {
        if (firstUnreadRef.current) {
          firstUnreadRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const markMessagesAsRead = async () => {
    if (!user || !userId) return;

    await Promise.all([
      supabase
        .from('messages')
        .update({ read: true })
        .eq('recipient_id', user.id)
        .eq('sender_id', userId)
        .eq('read', false),
      supabase
        .from('notifications')
        .update({ read: true })
        .eq('recipient_id', user.id)
        .eq('sender_id', userId)
        .eq('read', false)
    ]);
  };

  const setupRealtimeSubscription = () => {
    if (!user || !userId) return;

    const channel = supabase
      .channel(`messages_${user.id}_${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `sender_id=eq.${userId}`,
        },
        (payload) => {
          setMessages((prev) => [...prev, payload.new as Message]);
          markMessagesAsRead();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !user || !userId) return;

    setSending(true);
    try {
      const { data, error } = await supabase
        .from('messages')
        .insert({
          sender_id: user.id,
          recipient_id: userId,
          content: newMessage.trim(),
        })
        .select()
        .single();

      if (error) throw error;

      setMessages((prev) => [...prev, data]);
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const parseMessageContent = (content: string) => {
    const linkRegex = /View here → (\/[^\s]+)|View video → (\/[^\s]+)/g;
    const parts: (string | React.ReactNode)[] = [];
    let lastIndex = 0;
    let match;

    while ((match = linkRegex.exec(content)) !== null) {
      if (match.index > lastIndex) {
        parts.push(content.slice(lastIndex, match.index));
      }

      const path = match[1] || match[2];
      
      parts.push(
        <Link
          key={match.index}
          to={path}
          className="text-primary hover:underline font-medium"
        >
          View here →
        </Link>
      );

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < content.length) {
      parts.push(content.slice(lastIndex));
    }

    return parts.length > 0 ? parts : content;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <ModernLoader />
      </div>
    );
  }

  return (
    <>
      <SEO title={`Message ${recipientProfile?.username || 'User'}`} description="Direct message thread" />
      <div className="min-h-screen bg-background flex flex-col">
        {/* Header */}
        <header className="border-b bg-card sticky top-0 z-10">
          <div className="container mx-auto px-4 py-3 flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/inbox')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <Link to={`/user/${recipientProfile?.username}`} className="flex items-center gap-3 flex-1">
              <Avatar className="h-10 w-10 border-2 border-gold/20">
                <AvatarImage src={recipientProfile?.avatar_url} className="object-cover" />
                <AvatarFallback className="bg-muted">
                  {recipientProfile?.username?.[0]?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-semibold">@{recipientProfile?.username}</p>
              </div>
            </Link>
          </div>
        </header>

        {/* Messages */}
        <main className="flex-1 overflow-y-auto">
          <div className="container mx-auto px-4 py-4 max-w-3xl">
            <div className="space-y-4">
              {messages.map((message, index) => {
                const isOwnMessage = message.sender_id === user?.id;
                const isFirstUnread = !message.read && message.recipient_id === user?.id && 
                  (index === 0 || messages[index - 1].read || messages[index - 1].recipient_id !== user?.id);
                
                return (
                  <div
                    key={message.id}
                    ref={isFirstUnread ? firstUnreadRef : null}
                    className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[70%] ${isOwnMessage ? 'order-2' : 'order-1'}`}>
                      <div
                        className={`rounded-2xl px-4 py-2 ${
                          isOwnMessage
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap break-words">
                          {parseMessageContent(message.content)}
                        </p>
                      </div>
                      <p className={`text-xs text-muted-foreground mt-1 ${isOwnMessage ? 'text-right' : 'text-left'}`}>
                        {formatDistanceToNow(new Date(message.created_at), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          </div>
        </main>

        {/* Input */}
        <footer className="border-t bg-background sticky bottom-0 pb-safe">
          <div className="container mx-auto px-4 py-3 max-w-3xl">
            <form onSubmit={handleSendMessage}>
              <div className="relative flex items-center">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Message..."
                  className="rounded-full border border-gold/50 bg-background pr-11 pl-4 h-9 focus-visible:ring-0 focus-visible:border-gold"
                  disabled={sending}
                />
                {newMessage.trim() && (
                  <Button 
                    type="submit" 
                    disabled={sending} 
                    size="icon"
                    className="absolute right-1 rounded-full h-7 w-7 shrink-0 animate-scale-in"
                  >
                    <Send className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </form>
          </div>
        </footer>
      </div>
    </>
  );
}
